import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';

import { NEEDS_REVIEW_KINDS } from '@demeter/lib/constants/transaction-kinds';
import {
	AccountScope,
	AttributionWindow,
	BenchmarkReturns,
	BenchmarkSeries,
	EstimatedDtdResult,
	FundDTO,
	FundOwnershipDTO,
	FundOwnershipStake,
	FundTransactionDTO,
	MarketClock,
	OrderListItem,
	OrderPlacement,
	OrderQueryParams,
	OrderRequest,
	OrderSummaryDTO,
	PaginatedResponse,
	PerformanceSeries,
	PerformanceSummary,
	PositionDTO,
	PositionsResult,
	Quote,
	SessionObservation,
	SignalAuditComparison,
	SignalObservation,
	StrategyAttributionResult,
	StrategyLedgerAdjustmentRequest,
	StrategyLedgerAttributionRequest,
	StrategyLedgerEntriesResponse,
	StrategyLedgerMoveRequest,
	StrategyLedgerReversalRequest,
	StrategyLedgerWriteResult,
	StrategySharesResponse,
	TransactionAnnotationRequest,
	TransactionKind,
	TransactionListResult,
	TransactionRequest,
	TransactionsQueryParams,
	TransactionSummaryResult,
	UpdateFundOwnershipRequest,
	UserSettings
} from '@demeter/lib/models';
import { OptionType } from '@demeter/lib/models/option';

import { environment } from '../../environments/environment';
import { AuthService } from '../auth';

export interface QueryParams {
	[param: string]: string | number | boolean;
}

// Common's OptionType (Common/src/Models/OptionDefinition.cs) is a string-wire enum
// (JsonStringEnumMemberName) everywhere EXCEPT the Azure Functions Worker's query-string
// binding: [FromQuery] OptionType on GetOptionQuotes routes through the worker's
// JsonPocoConverter, which requires the raw query value to be valid JSON — a bare 'CALL'
// token isn't, so it 500s. Sending the enum's integer wire value ('1'/'2') is what
// QueryParamUtil.Add(OptionType) (Common's own HTTP clients) already sends. Keep the
// mapping here so every getOptionQuote call site keeps passing the friendly union.
const OPTION_TYPE_WIRE_VALUE: Record<OptionType, string> = {
	CALL: '1',
	PUT: '2'
};

@Injectable({
	providedIn: 'root'
})
export class CoreApiService {
	private get ROOT_URL(): string {
		return `${environment.apiUrl}`;
	}

	constructor(
		private httpClient: HttpClient,
		private authService: AuthService
	) {}

	public getFunds(brokerId?: string): Observable<FundDTO[]> {
		const url = `/funds`;
		const queryParams: QueryParams = {};
		if (brokerId) {
			queryParams['brokerId'] = brokerId;
		}
		return this._get<FundDTO[]>(url, queryParams);
	}

	public getUserSettings(): Observable<UserSettings> {
		return this._get<UserSettings>('/user-settings');
	}

	public saveUserSettings(patch: Partial<UserSettings>): Observable<UserSettings> {
		return this._post<UserSettings>('/user-settings', patch);
	}

	public getPositions(accountId: string, brokerId?: string): Observable<PositionDTO[]> {
		const url = `/funds/${encodeURIComponent(accountId)}/positions`;
		const queryParams: QueryParams = {};
		if (brokerId) queryParams['brokerId'] = brokerId;
		return this._get<PositionDTO[]>(url, queryParams);
	}

	// A distinct method, not an overload: the return shape differs, and the existing
	// getPositions() keeps today's contract for any caller that wants it.
	public getPositionsPartial(
		accountId: string,
		brokerId?: string,
		brokerAccountId?: string
	): Observable<PositionsResult> {
		const url = `/funds/${encodeURIComponent(accountId)}/positions`;
		const queryParams: QueryParams = { allowPartial: 'true', withMarks: 'true' };
		if (brokerId) queryParams['brokerId'] = brokerId;
		if (brokerAccountId) queryParams['brokerAccountId'] = brokerAccountId;
		return this._get<PositionsResult>(url, queryParams);
	}

	public getPerformanceSummary(
		accountId: string,
		scope?: AccountScope | null
	): Observable<PerformanceSummary> {
		const url = `/funds/${encodeURIComponent(accountId)}/performance/summary`;
		return this._get<PerformanceSummary>(url, {
			grain: 'account',
			...this._scopeParams(scope)
		});
	}

	public getPerformanceSeries(
		accountId: string,
		from: string,
		to: string,
		scope?: AccountScope | null
	): Observable<PerformanceSeries> {
		const url = `/funds/${encodeURIComponent(accountId)}/performance/series`;
		return this._get<PerformanceSeries>(url, {
			from,
			to,
			grain: 'account',
			...this._scopeParams(scope)
		});
	}

	public getPerformanceDtd(
		fundId: string,
		scope?: AccountScope | null
	): Observable<EstimatedDtdResult> {
		const url = `/funds/${encodeURIComponent(fundId)}/performance/dtd`;
		return this._get<EstimatedDtdResult>(url, this._scopeParams(scope));
	}

	public getBenchmarkReturns(symbol: string): Observable<BenchmarkReturns> {
		return this._get<BenchmarkReturns>(`/performance/benchmark/${encodeURIComponent(symbol)}`);
	}

	public getBenchmarkDtd(symbol: string): Observable<BenchmarkReturns> {
		return this._get<BenchmarkReturns>(
			`/performance/benchmark/${encodeURIComponent(symbol)}/dtd`
		);
	}

	public getBenchmarkSeries(
		symbol: string,
		from: string,
		to: string
	): Observable<BenchmarkSeries> {
		return this._get<BenchmarkSeries>(
			`/performance/benchmark/${encodeURIComponent(symbol)}/series`,
			{ from, to }
		);
	}

	public getMarketStatus(): Observable<MarketClock> {
		return this._get<MarketClock>('/market/status');
	}

	public getTransactions(
		accountId: string,
		from: string,
		to: string,
		kinds?: TransactionKind[],
		scope?: AccountScope | null
	): Observable<TransactionListResult> {
		const url = `/funds/${encodeURIComponent(accountId)}/transactions`;
		const queryParams: QueryParams = { from, to, ...this._scopeParams(scope) };
		if (kinds?.length) queryParams['kinds'] = kinds.join(',');
		return this._get<TransactionListResult>(url, queryParams);
	}

	public getUnclassifiedTransactions(
		accountId: string,
		from: string,
		to: string,
		scope?: AccountScope | null
	): Observable<TransactionListResult> {
		return this.getTransactions(accountId, from, to, NEEDS_REVIEW_KINDS, scope);
	}

	public getTransactionsSummary(
		accountId: string,
		from: string,
		to: string,
		scope?: AccountScope | null
	): Observable<TransactionSummaryResult> {
		const url = `/funds/${encodeURIComponent(accountId)}/transactions/summary`;
		return this._get<TransactionSummaryResult>(url, { from, to, ...this._scopeParams(scope) });
	}

	public getTransactionsPaged(
		params: TransactionsQueryParams
	): Observable<PaginatedResponse<FundTransactionDTO>> {
		const q: QueryParams = { from: params.from, to: params.to };
		const csv = (key: string, v?: string[]) => {
			if (v?.length) q[key] = v.join(',');
		};
		csv('fundIds', params.fundIds);
		csv('brokerIds', params.brokerIds);
		csv('brokerAccountIds', params.brokerAccountIds);
		csv('kinds', params.kinds);
		if (params.direction) q['direction'] = params.direction;
		if (params.minAmount != null) q['minAmount'] = String(params.minAmount);
		if (params.maxAmount != null) q['maxAmount'] = String(params.maxAmount);
		if (params.search) q['search'] = params.search;
		if (params.annotation) q['annotation'] = params.annotation;
		if (params.sortBy) q['sortBy'] = params.sortBy;
		if (params.sortDirection) q['sortDirection'] = params.sortDirection;
		if (params.page != null) q['page'] = String(params.page);
		if (params.pageSize != null) q['pageSize'] = String(params.pageSize);
		return this._get<PaginatedResponse<FundTransactionDTO>>('/transactions', q);
	}

	public postTransaction(
		accountId: string,
		body: TransactionRequest
	): Observable<{ sourceRef: string }> {
		const url = `/funds/${encodeURIComponent(accountId)}/transactions`;
		return this._post<{ sourceRef: string }>(url, body);
	}

	public upsertTransactionAnnotation(
		brokerId: string,
		sourceRef: string,
		body: TransactionAnnotationRequest
	): Observable<{ saved: boolean; transactionFound: boolean }> {
		const url = `/transactions/${encodeURIComponent(brokerId)}/${encodeURIComponent(sourceRef)}/annotation`;
		return this._post<{ saved: boolean; transactionFound: boolean }>(url, body);
	}

	public deleteTransactionAnnotation(brokerId: string, sourceRef: string): Observable<void> {
		const url = `/transactions/${encodeURIComponent(brokerId)}/${encodeURIComponent(sourceRef)}/annotation`;
		return this._delete<void>(url);
	}

	public getQuote(symbol: string): Observable<Quote> {
		const url = `/quotes/${encodeURIComponent(symbol)}`;
		const queryParams: QueryParams = symbol.startsWith('/') ? { isFuture: true } : {};
		return this._get<Quote>(url, queryParams);
	}

	public getOptionQuote(
		underlying: string,
		expiration: string,
		strike: number,
		type: OptionType
	): Observable<Quote> {
		const url = `/options/quotes/${encodeURIComponent(underlying)}`;
		const queryParams: QueryParams = {
			expiration,
			strike: String(strike),
			type: OPTION_TYPE_WIRE_VALUE[type]
		};
		return this._get<Quote>(url, queryParams);
	}

	// The filter and broker-scoping half of the orders query, shared by the grouped list and the
	// member list so a member's matchesFilter can never follow a different rule than the page.
	// Params are added only when present (an undefined would serialize as the string "undefined").
	private _orderFilterParams(params: OrderQueryParams): QueryParams {
		const queryParams: QueryParams = {};
		if (params.brokerId) queryParams['brokerId'] = params.brokerId;
		if (params.brokerAccountId) queryParams['brokerAccountId'] = params.brokerAccountId;
		const optional = [
			'symbol',
			'status',
			'side',
			'strategyId',
			'createdFrom',
			'createdTo'
		] as const;
		for (const k of optional) {
			const v = params[k];
			if (v != null) queryParams[k] = v as string;
		}
		return queryParams;
	}

	public getOrders(
		accountId: string,
		params: OrderQueryParams
	): Observable<PaginatedResponse<OrderListItem>> {
		const url = `/funds/${encodeURIComponent(accountId)}/orders`;
		const queryParams: QueryParams = {
			enrich: true,
			groupBy: 'plan',
			...this._orderFilterParams(params)
		};
		if (params.page != null) queryParams['page'] = params.page;
		if (params.pageSize != null) queryParams['pageSize'] = params.pageSize;
		if (params.sortBy) queryParams['sortBy'] = params.sortBy;
		if (params.sortDirection) queryParams['sortDirection'] = params.sortDirection;
		return this._get<PaginatedResponse<OrderListItem>>(url, queryParams);
	}

	// Core.API#147 member list: every order of one plan group, oldest first, each flagged with
	// matchesFilter against the page's filters.
	public getGroupMembers(
		fundId: string,
		planId: string,
		symbol: string,
		side: string,
		params: OrderQueryParams
	): Observable<PaginatedResponse<OrderListItem>> {
		const url = `/funds/${encodeURIComponent(fundId)}/orders`;
		const queryParams: QueryParams = {
			enrich: params.enrich ?? true,
			planId,
			groupSymbol: symbol,
			groupSide: side,
			...this._orderFilterParams(params)
		};
		return this._get<PaginatedResponse<OrderListItem>>(url, queryParams);
	}

	public getAttribution(
		fundId: string,
		attributionWindow: AttributionWindow,
		scope?: AccountScope | null
	): Observable<StrategyAttributionResult> {
		const url = `/funds/${encodeURIComponent(fundId)}/performance/attribution`;
		return this._get<StrategyAttributionResult>(url, {
			window: attributionWindow,
			...this._scopeParams(scope)
		});
	}

	// The fund page's account-picker equity: one DB-backed read of the fund's roster
	// (Core.API GET funds/{id}?useCoreDb=true). `useCoreDb` is a typed bool binding on the
	// server — it MUST be the lowercase literal 'true'.
	public getFundRoster(fundId: string): Observable<FundDTO> {
		return this._get<FundDTO>(`/funds/${encodeURIComponent(fundId)}`, { useCoreDb: 'true' });
	}

	public getOrder(
		fundId: string,
		orderId: string,
		brokerId?: string | null,
		includeEvents = true
	): Observable<OrderSummaryDTO> {
		const url = `/funds/${encodeURIComponent(fundId)}/orders/${encodeURIComponent(orderId)}`;
		const queryParams: QueryParams = { includeEvents };
		if (brokerId) queryParams['brokerId'] = brokerId;
		return this._get<OrderSummaryDTO>(url, queryParams);
	}

	public placeOrders(
		accountId: string,
		brokerId: string,
		orders: OrderRequest[],
		brokerAccountId?: string
	): Observable<OrderPlacement[]> {
		const accountQuery = brokerAccountId
			? `&brokerAccountId=${encodeURIComponent(brokerAccountId)}`
			: '';
		const url = `/funds/${encodeURIComponent(accountId)}/orders?brokerId=${encodeURIComponent(brokerId)}${accountQuery}`;
		return this._post<OrderPlacement[]>(url, orders);
	}

	public cancelOrder(accountId: string, brokerId: string, orderId: string): Observable<void> {
		const url = `/funds/${encodeURIComponent(accountId)}/orders/${encodeURIComponent(orderId)}`;
		return this._delete<void>(url, { brokerId });
	}

	// One read for the whole fund (Core.Trader#265). Never brokerId/brokerAccountId: a pair not on the
	// fund answers 400, and the page filter must not narrow the banner's count.
	public getStrategyShares(fundId: string): Observable<StrategySharesResponse> {
		return this._get<StrategySharesResponse>('/ledger/shares', { fundId });
	}

	public getStrategyLedgerEntries(
		brokerId: string,
		brokerAccountId: string,
		symbol?: string | null
	): Observable<StrategyLedgerEntriesResponse> {
		const queryParams: QueryParams = { brokerId, brokerAccountId };
		if (symbol) queryParams['symbol'] = symbol;
		return this._get<StrategyLedgerEntriesResponse>('/ledger/entries', queryParams);
	}

	public postLedgerAttribution(
		body: StrategyLedgerAttributionRequest
	): Observable<StrategyLedgerWriteResult> {
		return this._post<StrategyLedgerWriteResult>('/ledger/attributions', body);
	}

	public postLedgerAdjustment(
		body: StrategyLedgerAdjustmentRequest
	): Observable<StrategyLedgerWriteResult> {
		return this._post<StrategyLedgerWriteResult>('/ledger/adjustments', body);
	}

	public postLedgerMove(body: StrategyLedgerMoveRequest): Observable<StrategyLedgerWriteResult> {
		return this._post<StrategyLedgerWriteResult>('/ledger/moves', body);
	}

	public postLedgerReversal(
		body: StrategyLedgerReversalRequest
	): Observable<StrategyLedgerWriteResult> {
		return this._post<StrategyLedgerWriteResult>('/ledger/reversals', body);
	}

	public getOwnership(fundId: string, asOf?: string): Observable<FundOwnershipDTO> {
		const url = `/ownership/${encodeURIComponent(fundId)}`;
		const queryParams: QueryParams = {};
		if (asOf) {
			queryParams['asOf'] = asOf;
		}
		return this._get<FundOwnershipDTO>(url, queryParams);
	}

	public getOwnershipDirectory(asOf?: string): Observable<FundOwnershipDTO[]> {
		const url = `/ownership`;
		const queryParams: QueryParams = {};
		if (asOf) {
			queryParams['asOf'] = asOf;
		}
		return this._get<FundOwnershipDTO[]>(url, queryParams);
	}

	public getOwnershipHistory(fundId: string): Observable<FundOwnershipStake[]> {
		const url = `/ownership/${encodeURIComponent(fundId)}/history`;
		return this._get<FundOwnershipStake[]>(url, {});
	}

	public getLatestSignal(signalId: string): Observable<SignalObservation> {
		// environment.apiUrl is the APIM gateway and ALREADY ends in /v1 — a path carrying its own
		// v1 would emit /prod/v1/v1/signals/... and 404. Keep this a single template literal or the
		// generated consumption map renders "GET UNRESOLVED".
		const url = `/signals/${encodeURIComponent(signalId)}/latest`;
		return this._get<SignalObservation>(url);
	}

	public updateOwnership(fundId: string, body: UpdateFundOwnershipRequest): Observable<void> {
		const url = `/ownership/${encodeURIComponent(fundId)}`;
		return this._put<void>(url, body);
	}

	public getSessionObservations(sessionDate: string): Observable<SessionObservation[]> {
		return this._get<SessionObservation[]>('/session-observations', { sessionDate });
	}

	public getSessionAudits(sessionDate?: string): Observable<SignalAuditComparison[]> {
		// The params object is built only when a date is given: an `undefined` value would
		// serialize into the query.
		const queryParams: QueryParams = {};
		if (sessionDate) {
			queryParams['sessionDate'] = sessionDate;
		}
		return this._get<SignalAuditComparison[]>('/session-audits', queryParams);
	}

	public getSignalAudits(
		signalId: string,
		from: string,
		to: string
	): Observable<SignalAuditComparison[]> {
		const url = `/signals/${encodeURIComponent(signalId)}/audit`;
		return this._get<SignalAuditComparison[]>(url, { from, to });
	}

	private _post<T>(url: string, body: any): Observable<T> {
		return this.authService.getAccessToken().pipe(
			switchMap(token => {
				return this.httpClient.post<T>(`${this.ROOT_URL}${url}`, body, {
					headers: { Authorization: `Bearer ${token}` }
				});
			})
		);
	}

	private _put<T>(url: string, body: any): Observable<T> {
		return this.authService.getAccessToken().pipe(
			switchMap(token => {
				return this.httpClient.put<T>(`${this.ROOT_URL}${url}`, body, {
					headers: { Authorization: `Bearer ${token}` }
				});
			})
		);
	}

	// Both keys or neither — never an `undefined` value (HttpParams would send "undefined").
	private _scopeParams(scope?: AccountScope | null): QueryParams {
		return scope ? { brokerId: scope.brokerId, brokerAccountId: scope.brokerAccountId } : {};
	}

	private _get<T>(url: string, queryParams?: QueryParams): Observable<T> {
		return this.authService.getAccessToken().pipe(
			switchMap(token => {
				return this.httpClient.get<T>(`${this.ROOT_URL}${url}`, {
					headers: { Authorization: `Bearer ${token}` },
					params: new HttpParams({ fromObject: queryParams as Record<string, string> })
				});
			})
		);
	}

	private _delete<T>(url: string, queryParams?: QueryParams): Observable<T> {
		return this.authService.getAccessToken().pipe(
			switchMap(token => {
				return this.httpClient.delete<T>(`${this.ROOT_URL}${url}`, {
					headers: { Authorization: `Bearer ${token}` },
					params: new HttpParams({ fromObject: queryParams as Record<string, string> })
				});
			})
		);
	}
}
